import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { LOAD, LOAD_END, UPDATE_ENTRIES } from '../../../../../../../store/actions/actionsTypes'
import { IMiunData } from '../../../../../../../store/reducers/miunReducer'
import { RootState } from '../../../../../../../store/reducers/rootReducer'
import { Miun } from '../../../../../../modules/API/Miun/miun'
import { IEntriesItem } from '../../../../../../modules/API/Miun/models/sortingList'
import { IPutDivideData } from '../../../../../../modules/API/Miun/models/fetchData'
import { Defaults } from '../../../../../../modules/API/Defaults/defaults'
import { IWarhouseListItem } from '../../../../../../modules/API/Defaults/models/warhouseList'
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages'
import { ContextModal } from '../../../../../../modules/modal/modalContext'
import { addIDForTheList } from '../../../../../../generic/utils/addUIDForTheList'
import { LINKS } from '../../../../../../Routs/config'
import useDataFetcher from '../../../knisot/hooks/useFetchData'
import useMiunDateRange from './useMiunDateRange'

export type MiunTab = 'במיון' | 'למיון'

export interface ScanRef {
    focusInput: () => void
    blurInput: () => void
}

const useCreateMiun = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [modal, setModal] = useContext(ContextModal)
    const { from, to } = useMiunDateRange()

    const storedData = useSelector(
        (state: RootState): IMiunData => state.miunReducer,
    )
    const selectedSortingItem = storedData.selectedSortingItem

    const packageList: IPackagesItem[] = useSelector(
        (state: RootState) => state.defaults.packageList,
    )
    const { data: warehouseList } = useDataFetcher<IWarhouseListItem[]>(
        Defaults.getWarhouseList,
        'data',
    )

    const initialGivenState = useMemo<IEntriesItem[]>(() => {
        if (!selectedSortingItem?.Entries) return []
        return addIDForTheList(
            selectedSortingItem.Entries.map((e) => {
                e.isSelected = true
                e.Given = true
                e.Type = 'במיון'
                return e
            }),
        )
    }, [selectedSortingItem])

    const [input, setInput] = useState<string>('')
    const [entries, setEntries] = useState<IEntriesItem[]>()
    const [givenEntries, setGivenEntries] = useState<IEntriesItem[]>(initialGivenState)
    const [isOpenDivideModal, setIsOpenDivideModal] = useState(false)
    const [selectedItemForDivide, setSelectedItemForDivide] = useState<IEntriesItem>()
    const [selectedTab, setSelectedTab] = useState<MiunTab>('למיון')

    const refBody = useRef<ScanRef | null>(null)

    const showErrorModal = useCallback(
        (msg: string) => {
            setModal({ open: true, value: msg, mood: false })
            setTimeout(() => setModal({ ...modal, open: false }), 3000)
        },
        [setModal, modal],
    )

    const handleOpenDivideModal = useCallback(() => {
        setIsOpenDivideModal((prev) => !prev)
    }, [])

    const selectForDivide = useCallback(
        (item: IEntriesItem) => {
            if (item.AmountPack <= 1) {
                showErrorModal('can not divided')
                return
            }
            setSelectedItemForDivide(item)
            setIsOpenDivideModal(true)
        },
        [showErrorModal],
    )

    const moveItem = useCallback(
        (item: IEntriesItem, given?: boolean) => {
            if (!entries) return
            let unLinked = [...entries]
            let linked = [...givenEntries]

            if (!given) {
                unLinked = unLinked.filter(
                    (e) => !(e.DetailNum === item.DetailNum && e.EntryNum === item.EntryNum),
                )
                linked.push({ ...item, Given: true, Type: 'במיון' })
            } else {
                linked = linked.filter(
                    (e) => !(e.DetailNum === item.DetailNum && e.EntryNum === item.EntryNum),
                )
                unLinked.push({ ...item, Given: false, Type: 'למיון' })
            }
            setEntries(unLinked)
            setGivenEntries(linked)
            dispatch({ type: UPDATE_ENTRIES, payload: linked })
        },
        [entries, givenEntries, dispatch],
    )

    const MoveEntriesAPI = useCallback(
        async (item: IEntriesItem, given?: boolean) => {
            const dataFetch = {
                DetailNum: item.DetailNum,
                EntryNum: item.EntryNum,
                Gross: item.Gross,
                SortNum: selectedSortingItem?.Sorting_Num || 0,
            }
            try {
                await (given ? Miun.unLinkEntrie(dataFetch) : Miun.linkEntrie(dataFetch))
                moveItem(item, given)
            } catch {
                /* noop */
            }
        },
        [selectedSortingItem, moveItem],
    )

    const fetchDivide = useCallback(
        async (data: IPutDivideData, restItem: IEntriesItem, item: IEntriesItem) => {
            dispatch({ type: LOAD })
            try {
                const res: IEntriesItem = await Miun.putDivideEntrie(data)
                const toggle = restItem.Given
                if (!entries) return
                const initialArr = toggle ? givenEntries : entries
                const newArr = [...initialArr].map((it) => {
                    if (it.EntryNum === restItem.EntryNum && it.DetailNum === restItem.DetailNum) {
                        return {
                            ...restItem,
                            Gross: Math.round(it.Gross - data.Gross),
                            isSelected: it.isSelected,
                        }
                    }
                    return it
                })
                const pushItem = addIDForTheList([
                    {
                        ...res,
                        isSelected: item.isSelected,
                        Given: restItem.Given,
                        Type: 'למיון',
                    },
                ])
                newArr.push(...pushItem)
                if (toggle) setGivenEntries(newArr)
                else setEntries(newArr)
                setIsOpenDivideModal(false)
            } catch {
                /* noop */
            } finally {
                dispatch({ type: LOAD_END })
            }
        },
        [dispatch, entries, givenEntries],
    )

    const saveDevidedItem = useCallback(
        (item: IEntriesItem, qty: number, restItem: IEntriesItem) => {
            if (qty < 1 || item.AmountPack <= qty) {
                showErrorModal('can not divided')
                return
            }
            const putData = {
                AmountPack: +qty,
                DetailNum: item.DetailNum,
                EntryNum: item.EntryNum,
                Gross: Math.round((item.Gross / item.AmountPack) * qty),
                OutPackId: item.OutPackId,
            }
            fetchDivide(putData, restItem, item)
        },
        [fetchDivide, showErrorModal],
    )

    const changeWeight = useCallback(
        async (item: IEntriesItem, qty: number) => {
            const dataFetch = {
                DetailNum: item.DetailNum,
                EntryNum: item.EntryNum,
                Gross: qty,
                OutPackId: item.OutPackId,
            }
            try {
                await Miun.changeEntrieData(dataFetch)
                if (!entries) return
                const temp = item.Given ? [...givenEntries] : [...entries]
                const arr = temp.map((e) => {
                    if (e.EntryNum === item.EntryNum && e.DetailNum === item.DetailNum) {
                        e.Gross = qty
                    }
                    return e
                })
                if (item.Given) setGivenEntries(arr)
                else setEntries(arr)
            } catch {
                /* noop */
            }
        },
        [entries, givenEntries],
    )

    useEffect(() => {
        ;(async () => {
            if (!selectedSortingItem) return
            dispatch({ type: LOAD })
            try {
                const res = await Miun.getEtriesForSortingByNum(
                    selectedSortingItem.Sorting_Num,
                )
                setEntries(
                    addIDForTheList(res).map((e) => {
                        e.Selected = false
                        e.Type = 'למיון'
                        return e
                    }),
                )
            } catch {
                /* noop */
            } finally {
                dispatch({ type: LOAD_END })
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const renderEntries = useMemo(() => {
        if (!entries) return []
        let unionArr = [...entries, ...givenEntries]
        if (selectedTab) unionArr = unionArr.filter((e) => e.Type === selectedTab)
        const q = input.trim().toLowerCase()
        if (q) {
            unionArr = unionArr.filter((item) =>
                item.EntryDetailNum?.toLowerCase().includes(q),
            )
        }
        return unionArr
    }, [input, entries, givenEntries, selectedTab])

    const handleBack = useCallback(() => {
        const sortingDate = selectedSortingItem?.Date_Sorting
        const newTo = sortingDate && +sortingDate > +to ? sortingDate : to
        navigate(
            `${LINKS.miun.control.mainURL}?from=${from}&to=${newTo}`,
            { replace: true },
        )
    }, [navigate, selectedSortingItem, from, to])

    return {
        refBody,
        selectedSortingItem,
        input,
        setInput,
        selectedTab,
        setSelectedTab,
        renderEntries,
        entries,
        givenEntries,
        isOpenDivideModal,
        handleOpenDivideModal,
        selectedItemForDivide,
        selectForDivide,
        MoveEntriesAPI,
        changeWeight,
        saveDevidedItem,
        handleBack,
        warehouseList: warehouseList || [],
        packageList: packageList || [],
    }
}

export default useCreateMiun
